// Run-screen map: the GPU ocean (PixiRenderer) with the route line, weather cells and the position marker drawn
// as Pixi Graphics in world space. Day/night follows the simulation clock; reduced motion renders a still frame.
import { Graphics, isWebGLSupported } from 'pixi.js';
import { PixiRenderer } from '../../pixiRenderer';
import { skyPalette } from '../../palette';
import { makeOceanTile } from './oceanTile';
import type { Route } from '../types';

export const PX_PER_NM = 4;
/** The route bends gently: ±60 nm of lateral offset over a 2,400 nm wavelength. */
const AMP_NM = 60;
const WAVE_NM = 2400;
const CELL_RADIUS_NM = 25;
const AMBER = 0xd7b46b, INK = 0x1a1408, LINE = 0x93b7cc;

/** World-space point and heading (radians) of a position along the track. Pure. */
export function routePoint(nm: number): { x: number; y: number; heading: number } {
  const k = (2 * Math.PI) / WAVE_NM;
  const x = nm * PX_PER_NM;
  const y = AMP_NM * PX_PER_NM * Math.sin(nm * k);
  const dy = AMP_NM * PX_PER_NM * k * Math.cos(nm * k);
  return { x, y, heading: Math.atan2(dy, PX_PER_NM) };
}

export class OceanMap {
  private renderer: PixiRenderer;
  private readonly route: Route;
  private readonly reduced: boolean;
  private marker: Graphics | null = null;
  /** Camera zoom, fixed per map: the sea texture reads finer from altitude; marker and lines are scaled back to screen size. */
  private readonly zoom: number;
  private trail: Graphics | null = null;
  private lastTrailNm = -1;
  private lastStillRender = -Infinity;
  private destroyed = false;

  private constructor(renderer: PixiRenderer, route: Route, reduced: boolean) {
    this.renderer = renderer;
    this.route = route;
    this.reduced = reduced;
    this.zoom = window.innerWidth < 900 ? 0.45 : 0.55;
  }

  /**
   * Rejects when WebGL is unavailable; the run screen then shows a static fallback. The check is explicit because
   * Pixi 8 would otherwise fall back to its Canvas2D renderer, which drops the additive glints and the day/night wash.
   */
  static async create(host: HTMLElement, route: Route, reduced: boolean): Promise<OceanMap> {
    if (!isWebGLSupported()) throw new Error('WebGL is not available');
    const anchor = document.createElement('canvas');
    anchor.width = anchor.height = 1;
    anchor.style.display = 'none';
    anchor.setAttribute('aria-hidden', 'true');
    host.appendChild(anchor);
    const renderer = await PixiRenderer.create(anchor, makeOceanTile());
    const map = new OceanMap(renderer, route, reduced);
    map.drawRoute();
    return map;
  }

  private drawRoute(): void {
    const r = this.renderer;
    r.worldBegin();
    const g = r.worldGraphics('route', 1);
    if (g) {
      const len = this.route.lengthNm;
      const p0 = routePoint(0);
      g.moveTo(p0.x, p0.y);
      for (let nm = 10; nm <= len; nm += 10) { const p = routePoint(nm); g.lineTo(p.x, p.y); }
      g.stroke({ width: 3 / this.zoom, color: LINE, alpha: 0.55 });
      for (const seg of this.route.segments) {
        const p = routePoint(seg.fromNm);
        const t = 7 / this.zoom;
        const nx = -Math.sin(p.heading) * t, ny = Math.cos(p.heading) * t;
        g.moveTo(p.x - nx, p.y - ny).lineTo(p.x + nx, p.y + ny).stroke({ width: 2 / this.zoom, color: LINE, alpha: 0.5 });
        if (seg.weatherCell) {
          const m = routePoint((seg.fromNm + seg.toNm) / 2);
          g.circle(m.x, m.y, CELL_RADIUS_NM * PX_PER_NM).fill({ color: LINE, alpha: 0.16 }).stroke({ width: 2 / this.zoom, color: AMBER, alpha: 0.7 });
        }
      }
      g.circle(p0.x, p0.y, 9 / this.zoom).stroke({ width: 3 / this.zoom, color: AMBER, alpha: 0.9 });
    }
    this.trail = r.worldGraphics('trail', 2);
    this.marker = r.worldGraphics('marker', 3);
    r.worldEnd();
  }

  private drawTrail(positionNm: number): void {
    if (!this.trail || Math.abs(positionNm - this.lastTrailNm) < 2) return;
    this.lastTrailNm = positionNm;
    const g = this.trail;
    g.clear();
    const p0 = routePoint(0);
    g.moveTo(p0.x, p0.y);
    for (let nm = 10; nm < positionNm; nm += 10) { const p = routePoint(nm); g.lineTo(p.x, p.y); }
    const end = routePoint(positionNm);
    g.lineTo(end.x, end.y).stroke({ width: 3 / this.zoom, color: AMBER, alpha: 0.9 });
  }

  private drawMarker(positionNm: number): void {
    if (!this.marker) return;
    const p = routePoint(positionNm);
    const c = Math.cos(p.heading), s = Math.sin(p.heading), k = 1 / this.zoom;
    const pt = (lx: number, ly: number): number[] => [p.x + (lx * c - ly * s) * k, p.y + (lx * s + ly * c) * k];
    const g = this.marker;
    g.clear();
    g.circle(p.x, p.y, 22 * k).fill({ color: AMBER, alpha: 0.18 });
    g.poly([...pt(16, 0), ...pt(-10, 9), ...pt(-5, 0), ...pt(-10, -9)]).fill(AMBER).stroke({ width: 2 * k, color: INK });
  }

  /** Render one frame. `realS` drives the ambient water motion, `simTimeS` the time of day. */
  frame(realS: number, simTimeS: number, positionNm: number): void {
    if (this.destroyed) return;
    if (this.reduced && realS - this.lastStillRender < 2) return;
    this.lastStillRender = realS;
    const p = routePoint(positionNm);
    const phase = (simTimeS / 86400) % 1;
    const pal = skyPalette('cycle', phase);
    const W = window.innerWidth, H = window.innerHeight;
    this.drawTrail(positionNm);
    this.drawMarker(positionNm);
    this.renderer.render(p.x, p.y, this.zoom, this.reduced ? 0 : realS, W, H, pal.sea, pal.night, Math.floor(phase * 96));
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.renderer.destroy();
  }
}
